import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Dropdown from 'components/common/dropdown-component';
import { getProjectNameMenuOptions } from 'constants/feature/statement-creation-constants';
import { ProjectList } from 'models/api/project-list-model';
import { getProjectsInParticularGeo } from 'api/project-list-api';
import Radio from 'components/common/radio-component';
import ConditionalRender from 'components/util/conditional-render-component';
import { StatementList } from 'models/api/statement-list-model';
import { getStatementNameMenuOptions } from 'constants/util/side-by-side-utils';
import { useParams } from 'react-router-dom';
import { getStatementListForProjectRequest } from 'api/statement-list-api';
import { FormattedMessage } from 'react-intl';
import { setSideBySideViewParams } from 'utils/statement-content-page-utils';
import { isArray } from 'lodash';
import SelectedStatement from 'models/api/selected-statement-model';
import { SIDE_BY_SIDE_COMPARE_MODE } from 'constants/feature/statement-content-constants';

export const SELECT_STATEMENT_TAB_BLOCK = 'left-side-view-select-statement';
export const SELECT_STATEMENT_TAB_ID = 'left-side-view-select-statement-id';

const SelectStatement = ({
  isButtonDisabled,
  setSourceStatementParamsValues,
  selectedProjectData,
  sideBySideViewMode,
  linkedStatements,
  isRadiobuttonDisabled,
  setDisableConfirmButton,
  isTrailBalanceMode,
  isAutoTieoutCompareMode,
  isStatementWrappedOrPendingApproval, // target/current statement is in wrapped or pending approval state
  editPermissionAutotieout,
}) => {
  const { projectId, statementId } = useParams();
  const [projectList, setProjectList] = useState(new ProjectList());
  const [selectedProject, setSelectedProject] = useState(null);
  const [statementList, setStatementList] = useState(new StatementList());
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [selectedStatementProcessType, setSelectedStatementProcessType] =
    useState(1);

  const [selectedStatementRevision, setSelectedStatementRevision] =
    useState(null);

  const [projectOptions, setProjectOptions] = useState([]);
  const [statementOptions, setStatementOptions] = useState([]);

  useEffect(() => {
    const _fetchProjectList = async () => {
      try {
        setProjectList(projectList.setLoading());
        const response = await getProjectsInParticularGeo();
        response.data.result = isArray(response.data.result)
          ? response.data.result.filter(
              (_project) =>
                _project.containerCode === selectedProjectData.containerCode,
            )
          : [];

        setProjectList(projectList.setLoaded({ response }));
      } catch (error) {
        setProjectList(projectList.setError(error));
      }
    };

    _fetchProjectList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedProjectData.containerCode]);

  useEffect(() => {
    if (projectList.hasProjects()) {
      //set project options
      const projectMenuOptions = getProjectNameMenuOptions(
        projectList.getActiveProjects(),
        true,
      );
      setProjectOptions(projectMenuOptions);
      // set default project
      setSelectedProject(
        // eslint-disable-next-line eqeqeq
        projectMenuOptions.find((project) => project.id == projectId),
      );
    }
  }, [projectList, projectId]);

  useEffect(() => {
    const _fetchStatementList = async () => {
      try {
        setStatementList(statementList.setLoading());
        const response = await getStatementListForProjectRequest({
          projectId: selectedProject.id,
          selectedProject: selectedProjectData,
        });
        if (
          response &&
          response.data &&
          response.data.result &&
          response.data.result.elements &&
          response.data.result.elements.length
        ) {
          response.data.result.elements.forEach((item, index, arr) => {
            if (item.id === parseInt(statementId)) {
              arr.splice(index, 1);
            }
          });
        }

        setStatementList(statementList.setLoaded({ response }));
      } catch (error) {
        setStatementList(statementList.setError(error));
      }
    };

    selectedProject && selectedProject.id && _fetchStatementList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  useEffect(() => {
    setSourceStatementParamsValues(
      setSideBySideViewParams(
        selectedProject && selectedProject.id,
        selectedStatement,
        selectedStatementRevision,
        selectedStatementProcessType,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedStatement, selectedStatementRevision]);

  const onStatementSelection = (val) => {
    setSelectedStatement(val);
    setSelectedStatementRevision(
      statementList.getStatements().find((item) => item.id === val).revisionId,
    );
    setSelectedStatementProcessType(
      statementList.getStatements().find((item) => item.id === val)
        .documentProcessType,
    );
    isAutoTieoutCompareMode &&
      isStatementWrappedOrPendingApproval &&
      setDisableConfirmButton(isStatementWrappedOrPendingApproval);
  };

  const onProjectSelection = (val) => {
    setSelectedProject(val);
    isButtonDisabled(true);
    setSelectedStatement(null);
    setSelectedStatementRevision(null);
    setSelectedStatementProcessType(null);
    setStatementList(new StatementList());
    statementOptions.current = [];
  };

  useEffect(() => {
    if (statementList.hasStatements()) {
      // set statement options
      const revisionIdToLinkedStatementsMap =
        linkedStatements.revisionIdToStatementMap;
      const statementMenuOptions = getStatementNameMenuOptions(
        statementList.getStatements(),
        revisionIdToLinkedStatementsMap,
      );
      const firstEnabledStatement = statementList
        .getStatements()
        .find((item) => !revisionIdToLinkedStatementsMap.has(item.revisionId));

      setStatementOptions(statementMenuOptions);
      if (firstEnabledStatement) {
        setSelectedStatement(firstEnabledStatement.id);
        setSelectedStatementRevision(firstEnabledStatement.revisionId);
        setSelectedStatementProcessType(
          firstEnabledStatement.documentProcessType,
        );
        isButtonDisabled(false);
        setDisableConfirmButton(isStatementWrappedOrPendingApproval);
      }
    }
  }, [statementList, linkedStatements]);

  const shouldDisableRadioButton =
    (isTrailBalanceMode || isAutoTieoutCompareMode) &&
    (isRadiobuttonDisabled ||
      isStatementWrappedOrPendingApproval ||
      !editPermissionAutotieout);

  return (
    <>
      <ConditionalRender dependencies={[projectList]}>
        <Dropdown
          id={`${SELECT_STATEMENT_TAB_ID}-project-list`}
          className={`${SELECT_STATEMENT_TAB_BLOCK}_project_list-dropdown`}
          label={'side-by-side-view.compare.left-panel.dropdown.label'}
          placeholder={'common.placeholder.select'}
          width={'100%'}
          onSelectOption={(option) => onProjectSelection(option)}
          options={projectOptions}
          value={selectedProject}
        />
      </ConditionalRender>
      <ConditionalRender dependencies={[statementList]}>
        {statementList && statementList.hasStatements() ? (
          <Radio
            id={`${SELECT_STATEMENT_TAB_ID}-statements`}
            label={
              'side-by-side-view.compare.left-panel.statements.radio.label'
            }
            tooltip={{
              text: 'auto-tieout.statement.tab.radio.disabled.tooltip.message',
            }}
            className={classnames(
              `${SELECT_STATEMENT_TAB_BLOCK}_radio_wrap-statement-list`,
              {
                'radio-disabled-cursor': shouldDisableRadioButton,
              },
            )}
            radioWrapperClass={`${SELECT_STATEMENT_TAB_BLOCK}_radio`}
            options={statementOptions}
            width="auto"
            isValid
            selectedOption={selectedStatement}
            onChange={(val) =>
              !shouldDisableRadioButton && onStatementSelection(val)
            }
            disabled={shouldDisableRadioButton}
          />
        ) : (
          <div className={`${SELECT_STATEMENT_TAB_BLOCK}__no-data-msg`}>
            {sideBySideViewMode.viewMode === SIDE_BY_SIDE_COMPARE_MODE ? (
              <FormattedMessage id="side-by-side-view.compare.left-panel.data-not-found" />
            ) : (
              <FormattedMessage id="side-by-side-view.auto-tieout.compare.left-panel.data-not-found" />
            )}
          </div>
        )}
      </ConditionalRender>
    </>
  );
};

SelectStatement.propTypes = {
  /** func to set disable the confirm button */
  isButtonDisabled: PropTypes.func.isRequired,
  /** action is used to set projectId, statementId and revisionId as per selection of user in sideBySide view*/
  setSourceStatementParamsValues: PropTypes.func.isRequired,
  /** statement data of a target statement in side-by-side view page */
  selectedProjectData: PropTypes.instanceOf(SelectedStatement),
  /** boolean to indicate if radio button should be disabled */
  isRadiobuttonDisabled: PropTypes.bool,
};

export default SelectStatement;
